import { createApi } from "@reduxjs/toolkit/query/react"
import type { BaseQueryFn } from "@reduxjs/toolkit/query"
import type { AxiosError, AxiosRequestConfig } from "axios"
import $api from "../../../axios"
import {
  ApplyMutationsArgs,
  ApplyMutationsResponse,
  BootstrapResponse,
  CreateLabelArgs,
  DeleteExploreFilterArgs,
  DetailsResponse,
  GetBootstrapArgs,
  GetDetailsArgs,
  GetListArgs,
  Label,
  ListResponse,
  SaveSettingsArgs,
  SaveSettingsResponse,
} from "./types/searchTermsTrendsApi.types"
import { patchRows } from "./optimistic"

interface AxiosBaseQueryArgs {
  url: string
  method: AxiosRequestConfig["method"]
  data?: unknown
  params?: Record<string, unknown>
}

interface AxiosBaseQueryError {
  status: number | string
  data: unknown
}

const axiosBaseQuery: BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError> = async ({
  url,
  method,
  data,
  params,
}) => {
  try {
    const response = await $api({ url, method, data, params })
    return { data: response.data }
  } catch (error) {
    const axiosError = error as AxiosError
    return {
      error: {
        status: axiosError.response?.status ?? "FETCH_ERROR",
        data: axiosError.response?.data ?? axiosError.message,
      },
    }
  }
}

const BOOTSTRAP_TAG = "Bootstrap"
const LIST_TAG = "List"
const DETAILS_TAG = "Details"
const SETTINGS_TAG = "Settings"
const LABELS_TAG = "Labels"

export const searchTermsTrendsApi = createApi({
  reducerPath: "searchTermsTrendsApi",
  baseQuery: axiosBaseQuery,
  tagTypes: [BOOTSTRAP_TAG, LIST_TAG, DETAILS_TAG, SETTINGS_TAG, LABELS_TAG],
  endpoints: (builder) => ({
    getBootstrap: builder.query<BootstrapResponse, GetBootstrapArgs>({
      query: ({ reportType }) => ({
        url: "/searchTermsTrends/bootstrap",
        method: "get",
        params: { reportType },
      }),
      providesTags: (_result, _error, { reportType }) => [
        { type: BOOTSTRAP_TAG, id: reportType },
        { type: LIST_TAG, id: reportType },
        { type: SETTINGS_TAG, id: reportType },
        LABELS_TAG,
      ],
    }),

    getList: builder.query<ListResponse, GetListArgs>({
      query: ({ reportType, cursor, limit, state, labelIds, search }) => ({
        url: "/searchTermsTrends/list",
        method: "get",
        params: {
          reportType,
          cursor: cursor || undefined,
          limit,
          state: state || undefined,
          labelIds: labelIds && labelIds.length ? labelIds.join(",") : undefined,
          search: search || undefined,
        },
      }),
      providesTags: (_result, _error, { reportType }) => [{ type: LIST_TAG, id: reportType }],
    }),

    getDetails: builder.query<DetailsResponse, GetDetailsArgs>({
      query: ({ reportType, searchTerm, categoryId, include }) => ({
        url: "/searchTermsTrends/details",
        method: "get",
        params: { reportType, searchTerm, categoryId, include },
      }),
      providesTags: (_result, _error, { searchTerm, categoryId }) => [
        { type: DETAILS_TAG, id: searchTerm || categoryId || "current" },
      ],
    }),

    applyMutations: builder.mutation<ApplyMutationsResponse, ApplyMutationsArgs>({
      query: ({ reportType, ops }) => ({
        url: "/searchTermsTrends/mutations",
        method: "post",
        data: { reportType, ops },
      }),
      async onQueryStarted(
        { reportType, ops, listArgs, bootstrapArgs },
        { dispatch, queryFulfilled }
      ) {
        const undos: Array<{ undo: () => void }> = []

        if (listArgs) {
          const listPatch = dispatch(
            searchTermsTrendsApi.util.updateQueryData("getList", listArgs, (draft) => {
              draft.rows = patchRows(draft.rows, ops, reportType)
            })
          )
          undos.push(listPatch)
        }

        const bootstrapTarget = bootstrapArgs || { reportType }
        const bootstrapPatch = dispatch(
          searchTermsTrendsApi.util.updateQueryData("getBootstrap", bootstrapTarget, (draft) => {
            draft.list.rows = patchRows(draft.list.rows, ops, reportType)
          })
        )
        undos.push(bootstrapPatch)

        try {
          await queryFulfilled
        } catch {
          undos.forEach((entry) => entry.undo())
        }
      },
    }),

    saveSettings: builder.mutation<SaveSettingsResponse, SaveSettingsArgs>({
      query: ({ reportType, sort, exploreFilters }) => ({
        url: "/searchTermsTrends/settings",
        method: "post",
        data: { reportType, sort, exploreFilters },
      }),
      invalidatesTags: (_result, _error, { reportType }) => [
        { type: BOOTSTRAP_TAG, id: reportType },
        { type: LIST_TAG, id: reportType },
        { type: SETTINGS_TAG, id: reportType },
      ],
    }),

    deleteExploreFilter: builder.mutation<SaveSettingsResponse, DeleteExploreFilterArgs>({
      query: ({ reportType, id }) => ({
        url: `/searchTermsTrends/settings/exploreFilter/${id}`,
        method: "delete",
        params: { reportType },
      }),
      invalidatesTags: (_result, _error, { reportType }) => [
        { type: BOOTSTRAP_TAG, id: reportType },
        { type: LIST_TAG, id: reportType },
        { type: SETTINGS_TAG, id: reportType },
      ],
    }),

    getLabels: builder.query<Label[], void>({
      query: () => ({
        url: "/labels",
        method: "get",
      }),
      providesTags: [LABELS_TAG],
    }),

    createLabel: builder.mutation<Label, CreateLabelArgs>({
      query: (body) => ({
        url: "/labels",
        method: "post",
        data: body,
      }),
      invalidatesTags: [LABELS_TAG, BOOTSTRAP_TAG, LIST_TAG],
    }),

    removeLabel: builder.mutation<{ message: string }, { _id: string }>({
      query: ({ _id }) => ({
        url: "/labels/remove",
        method: "post",
        data: { _id },
      }),
      invalidatesTags: [LABELS_TAG, BOOTSTRAP_TAG, LIST_TAG],
    }),
  }),
})

export const {
  useGetBootstrapQuery,
  useGetListQuery,
  useLazyGetListQuery,
  useGetDetailsQuery,
  useApplyMutationsMutation,
  useSaveSettingsMutation,
  useDeleteExploreFilterMutation,
  useGetLabelsQuery,
  useCreateLabelMutation,
  useRemoveLabelMutation,
} = searchTermsTrendsApi
